<template>
  <div class="crm-busca-vinculo">
    <b-form-group :label="label">
      <b-form-input
        v-model="termo"
        :placeholder="placeholder"
        autocomplete="off"
        @input="debouncedBuscar"
      />
    </b-form-group>

    <div v-if="permiteCriarContato" class="crm-busca-toolbar mb-3">
      <button
        class="btn btn-light-primary btn-xs"
        type="button"
        @click="abrirCriacaoContato"
      >
        Criar contato
      </button>
    </div>

    <div v-if="selecionado" class="crm-busca-selecionado mb-3">
      <div>
        <small class="text-muted d-block">
          {{ tipoLabel(selecionado.tipo) }}
        </small>
        <strong>{{ selecionado.label }}</strong>
      </div>
      <div class="crm-busca-acoes">
        <button class="btn btn-light btn-xs" type="button" @click="limpar">
          Trocar
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-muted mb-3">Buscando...</div>
    <div v-else-if="erro" class="alert alert-light-danger py-2 mb-3">
      {{ erro }}
    </div>

    <div v-else-if="termoBuscado && !totalResultados" class="crm-busca-vazio">
      <span>
        Nenhum cliente ou contato encontrado para
        <strong>{{ termoBuscado }}</strong
        >.
      </span>
    </div>

    <div v-else-if="totalResultados" class="crm-busca-resultados">
      <div v-if="contatos.length" class="crm-busca-grupo">
        <small class="text-muted text-uppercase">Contatos CRM</small>
        <button
          v-for="contato in contatos"
          :key="'contato-' + contato.id_crm_contato"
          class="crm-busca-item"
          type="button"
          @click="selecionar('contato', contato)"
        >
          <strong>{{ contato.nome }}</strong>
          <small>{{ contatoDetalhe(contato) }}</small>
        </button>
      </div>

      <div v-if="pessoas.length" class="crm-busca-grupo">
        <small class="text-muted text-uppercase">Pessoas existentes</small>
        <button
          v-for="pessoa in pessoas"
          :key="'pessoa-' + pessoa.id_pessoa"
          class="crm-busca-item"
          type="button"
          @click="selecionar('pessoa', pessoa)"
        >
          <strong>{{ pessoa.nome || pessoa.nome_fantasia }}</strong>
          <small>{{ pessoaDetalhe(pessoa) }}</small>
        </button>
      </div>

      <div v-if="oportunidades.length" class="crm-busca-grupo">
        <small class="text-muted text-uppercase">Oportunidades abertas</small>
        <button
          v-for="oportunidade in oportunidades"
          :key="'oportunidade-' + oportunidade.id_crm_oportunidade"
          class="crm-busca-item"
          type="button"
          @click="selecionar('oportunidade', oportunidade)"
        >
          <strong>{{ oportunidade.titulo }}</strong>
          <small>
            {{ oportunidade.contato || oportunidade.pessoa || "Sem contato" }}
          </small>
        </button>
      </div>
    </div>

    <b-modal
      v-model="modalCriarContato"
      title="Criar contato"
      ok-title="Criar contato"
      cancel-title="Cancelar"
      @ok.prevent="confirmarCriacaoContato"
    >
      <b-form-group label="Nome">
        <b-form-input v-model="novoContato.nome" autocomplete="off" />
      </b-form-group>
      <b-form-group label="WhatsApp">
        <b-form-input
          v-model="novoContato.telefone"
          autocomplete="off"
          placeholder="Informe o telefone com DDD"
          type="tel"
          @keyup.enter="confirmarCriacaoContato"
        />
        <small class="text-muted">
          Telefone mínimo para abrir oportunidade com cliente novo.
        </small>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import crmService from "@/modules/CRM/services/crmService";

export default {
  name: "BuscaClienteContato",
  props: {
    label: {
      type: String,
      default: "Cliente ou contato",
    },
    placeholder: {
      type: String,
      default: "Busque por nome, telefone, e-mail ou documento",
    },
    incluirPessoas: {
      type: Boolean,
      default: true,
    },
    incluirContatos: {
      type: Boolean,
      default: true,
    },
    incluirOportunidades: {
      type: Boolean,
      default: false,
    },
    permiteCriarContato: {
      type: Boolean,
      default: true,
    },
    limit: {
      type: Number,
      default: 10,
    },
    value: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      contatos: [],
      erro: "",
      loading: false,
      modalCriarContato: false,
      novoContato: {
        nome: "",
        telefone: "",
      },
      oportunidades: [],
      pessoas: [],
      selecionado: this.value,
      termo: "",
      termoBuscado: "",
      timer: null,
    };
  },
  computed: {
    totalResultados() {
      return (
        this.contatos.length + this.pessoas.length + this.oportunidades.length
      );
    },
  },
  watch: {
    value(value) {
      this.selecionado = value;
    },
  },
  methods: {
    debouncedBuscar() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.buscar(), 350);
    },
    async buscar() {
      const termo = (this.termo || "").trim();
      this.erro = "";
      this.termoBuscado = "";
      this.contatos = [];
      this.pessoas = [];
      this.oportunidades = [];

      if (termo.length < 2) {
        return;
      }

      this.loading = true;
      try {
        const requests = [];
        const requestTypes = [];

        if (this.incluirContatos) {
          requests.push(
            crmService.contatosAutocomplete({
              busca: termo,
              status: "Todos",
              limit: this.limit,
            })
          );
          requestTypes.push("contatos");
        }

        if (this.incluirPessoas) {
          requests.push(
            crmService.pessoasAutocomplete({
              busca: termo,
              limit: this.limit,
            })
          );
          requestTypes.push("pessoas");
        }

        if (this.incluirOportunidades) {
          requests.push(
            crmService.oportunidades({
              page: 1,
              per_page: this.limit,
              columnFilters: {
                contato: termo,
                status: "aberta",
              },
            })
          );
          requestTypes.push("oportunidades");
        }

        const responses = await Promise.all(requests);
        responses.forEach((response, index) => {
          const type = requestTypes[index];

          if (type === "contatos") {
            this.contatos = this.normalizeCollection(response.data);
          }

          if (type === "pessoas") {
            this.pessoas = this.normalizeCollection(response.data);
          }

          if (type === "oportunidades") {
            this.oportunidades = this.normalizeCollection(response.data);
          }
        });

        this.termoBuscado = termo;
      } catch (error) {
        this.erro = "Não foi possível buscar clientes e contatos.";
      } finally {
        this.loading = false;
      }
    },
    selecionar(tipo, item) {
      const selecionado = {
        tipo,
        label: this.itemLabel(tipo, item),
        id_crm_contato:
          tipo === "contato"
            ? item.id_crm_contato
            : item.id_crm_contato || null,
        id_pessoa: tipo === "pessoa" ? item.id_pessoa : item.id_pessoa || null,
        id_crm_oportunidade:
          tipo === "oportunidade" ? item.id_crm_oportunidade : null,
        raw: item,
      };

      this.selecionado = selecionado;
      this.$emit("input", selecionado);
      this.$emit("select", selecionado);
    },
    limpar() {
      this.selecionado = null;
      this.$emit("input", null);
      this.$emit("select", null);
    },
    abrirCriacaoContato() {
      this.novoContato = {
        nome: ((this.termo || "").trim() || this.termoBuscado || "").trim(),
        telefone: "",
      };
      this.modalCriarContato = true;
    },
    confirmarCriacaoContato() {
      const nome = (this.novoContato.nome || "").trim();
      const telefone = (this.novoContato.telefone || "").trim();

      if (!nome) {
        this.$toast.error("Informe o nome do contato.");
        return;
      }

      if (!telefone) {
        this.$toast.error("Informe o WhatsApp do contato.");
        return;
      }

      this.modalCriarContato = false;
      this.$emit("criar-contato", {
        nome,
        telefone,
        origem_criacao_rapida: true,
      });
    },
    normalizeCollection(data) {
      if (Array.isArray(data)) {
        return data;
      }

      if (data && Array.isArray(data.data)) {
        return data.data;
      }

      return [];
    },
    itemLabel(tipo, item) {
      if (tipo === "oportunidade") {
        return item.titulo;
      }

      return item.nome || item.nome_fantasia || item.email || "Sem nome";
    },
    tipoLabel(tipo) {
      const labels = {
        contato: "Contato CRM",
        pessoa: "Pessoa existente",
        oportunidade: "Oportunidade aberta",
      };

      return labels[tipo] || "Vínculo";
    },
    contatoDetalhe(contato) {
      return [contato.telefone, contato.email, contato.documento]
        .filter(Boolean)
        .join(" · ");
    },
    pessoaDetalhe(pessoa) {
      return [pessoa.nome_fantasia, pessoa.cnpj_cpf || pessoa.cpf]
        .filter(Boolean)
        .join(" · ");
    },
  },
};
</script>

<style scoped>
.crm-busca-toolbar {
  display: flex;
  justify-content: flex-start;
}

.crm-busca-grupo + .crm-busca-grupo {
  margin-top: 12px;
}

.crm-busca-item {
  background: #ffffff;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  display: block;
  margin-top: 6px;
  padding: 9px 10px;
  text-align: left;
  width: 100%;
}

.crm-busca-item:hover {
  border-color: #3699ff;
}

.crm-busca-item small {
  color: #7e8299;
  display: block;
  margin-top: 2px;
  overflow-wrap: anywhere;
}

.crm-busca-item strong {
  display: block;
  overflow-wrap: anywhere;
}

.crm-busca-acoes {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.crm-busca-selecionado,
.crm-busca-vazio {
  align-items: center;
  background: #f3f6f9;
  border-radius: 6px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 10px 12px;
}

.crm-busca-selecionado > div,
.crm-busca-vazio > span {
  min-width: 0;
}

.crm-busca-selecionado strong,
.crm-busca-vazio strong {
  overflow-wrap: anywhere;
}

@media (max-width: 575px) {
  .crm-busca-selecionado,
  .crm-busca-vazio {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }

  .crm-busca-acoes {
    justify-content: flex-start;
  }
}
</style>
